import {FC, ReactNode, useState} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {useTranslation} from '@/i18n'
import {CONTAINER_PADDING, CONTAINER_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {Currency} from '@/utils/currency'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {PricingDiscountBanner} from './banner/PricingDiscountBanner'
import {PricingCountryCalculatorMobile} from './mobile/PricingCountryCalculatorMobile'
import {PricingCountryCalculator, PricingCountryCalculatorProps} from './PricingCountryCalculator'
import {JurisdictionPricingInfo} from './PricingCountryCheckboxList'

export type PricingEstimationCardProps = {
  selectedJurisdictions: Jurisdiction[]
  classesByJurisdiction: Partial<Record<Jurisdiction, number>>
  trademarksByJurisdiction: Partial<Record<Jurisdiction, number>>
  setClassesByJurisdiction: (
    updater: (prev: Partial<Record<Jurisdiction, number>>) => Partial<Record<Jurisdiction, number>>,
  ) => void
  setTrademarksByJurisdiction: (
    updater: (prev: Partial<Record<Jurisdiction, number>>) => Partial<Record<Jurisdiction, number>>,
  ) => void
}

type PricingCalculatorTableProps = {
  subtitle: string
  startButtonText: string
  continueButtonText: string
  getLinkHref: (
    selectedJurisdictions: Jurisdiction[],
    classesByJurisdiction: Partial<Record<Jurisdiction, number>>,
    trademarksByJurisdiction: Partial<Record<Jurisdiction, number>>,
  ) => string
  trackedEventName: string
  EstimationCard: FC<PricingEstimationCardProps>
  showDiscountBanner?: boolean
  getJurisdictionPricing: (
    jurisdiction: Jurisdiction,
    currency: Currency,
  ) => JurisdictionPricingInfo
  mobileChildren?: ReactNode
  allowedJurisdictions?: Jurisdiction[]
  allowEuMemberSelection: boolean
}

export const PricingCalculatorTable: FC<PricingCalculatorTableProps> = ({
  subtitle,
  startButtonText,
  continueButtonText,
  getLinkHref,
  trackedEventName,
  EstimationCard,
  showDiscountBanner,
  getJurisdictionPricing,
  mobileChildren,
  allowedJurisdictions,
  allowEuMemberSelection,
}) => {
  const {t} = useTranslation()
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<Jurisdiction[]>([])
  const [classesByJurisdiction, setClassesByJurisdiction] = useState<
    Partial<Record<Jurisdiction, number>>
  >({})
  const [trademarksByJurisdiction, setTrademarksByJurisdiction] = useState<
    Partial<Record<Jurisdiction, number>>
  >({})

  const linkHref = getLinkHref(
    selectedJurisdictions,
    classesByJurisdiction,
    trademarksByJurisdiction,
  )

  const trackClickEvent = () => {
    analytics.track(trackedEventName, {selectedCountries: selectedJurisdictions})
  }

  const estimationCard = (
    <EstimationCard
      selectedJurisdictions={selectedJurisdictions}
      classesByJurisdiction={classesByJurisdiction}
      trademarksByJurisdiction={trademarksByJurisdiction}
      setClassesByJurisdiction={setClassesByJurisdiction}
      setTrademarksByJurisdiction={setTrademarksByJurisdiction}
    />
  )
  const discountBanner = showDiscountBanner ? (
    <PricingDiscountBanner isDiscountApplied={selectedJurisdictions.length > 0} />
  ) : undefined

  const calculatorProps: PricingCountryCalculatorProps = {
    selectedJurisdictions,
    setSelectedJurisdictions,
    linkHref,
    trackClickEvent,
    startButtonText,
    continueButtonText,
    estimationCard,
    discountBanner,
    getJurisdictionPricing,
    allowedJurisdictions,
    allowEuMemberSelection,
  }

  return (
    <>
      <div className="subtitle">
        <Text variant={TextVariant.h3} element="h2" align="center">
          {t(subtitle)}
        </Text>
      </div>
      <div className="desktop">
        <PricingCountryCalculator {...calculatorProps} />
      </div>
      <div className="mobile">
        <PricingCountryCalculatorMobile {...calculatorProps}>
          {mobileChildren}
        </PricingCountryCalculatorMobile>
      </div>

      <style jsx>{`
        .subtitle {
          max-width: 750px;
          margin: ${theme.spacing[6.5]} auto ${theme.spacing[7]};
        }
        .mobile {
          display: none;
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .subtitle {
            max-width: none;
            padding: 0 ${CONTAINER_PADDING} ${theme.spacing[5]};
            margin: ${theme.spacing[5.5]} -${CONTAINER_PADDING} 0;
            border-bottom: 1px solid ${theme.palette.gray[200]};
          }
          .desktop {
            display: none;
          }
          .mobile {
            display: block;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .subtitle {
            padding: 0 ${CONTAINER_PADDING_MOBILE} ${theme.spacing[5]};
            margin: ${theme.spacing[5.5]} -${CONTAINER_PADDING_MOBILE} 0;
          }
        }
      `}</style>
    </>
  )
}
